/** Compact waveform payload shape generated by Podcasts::Episodes::AudioFiles::GenerateWaveform */
export type WaveformPayload = {
  v: number;
  encoding: 'u8-base64';
  bars: number;
  bits: number;
  samples_per_pixel: number | null;
  peaks: string;
};

/**
 * Decode a compact waveform payload into normalized bar heights (0..1).
 *
 * The backend encodes peaks as unsigned 8-bit integers (0–255) packed into
 * a base64 string. We decode to a Uint8Array and map each byte to a 0..1 float.
 *
 * Returns null if the payload is missing, malformed, or uses an unsupported encoding.
 */
export function decodeWaveformPayload(payload: unknown): number[] | null {
  if (!payload || typeof payload !== 'object') return null;

  const wf = payload as Record<string, unknown>;

  if (wf.encoding !== 'u8-base64' || typeof wf.peaks !== 'string' || !wf.peaks) {
    return null;
  }

  try {
    const binaryString = atob(wf.peaks);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Find the actual peak so we can scale to the full 0–1 range.
    // Raw audio often doesn't hit 255, so dividing by max instead of 255
    // ensures the tallest bar always reaches full height.
    let max = 0;
    for (let i = 0; i < bytes.length; i++) {
      if (bytes[i] > max) max = bytes[i];
    }
    const scale = max > 0 ? max : 1;

    const normalized: number[] = new Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      normalized[i] = bytes[i] / scale;
    }

    return normalized.length > 0 ? normalized : null;
  } catch {
    return null;
  }
}

/**
 * Resample a peaks array to a target bar count using linear interpolation.
 * Used when the decoded peaks don't match the number of bars the canvas needs.
 */
export function resamplePeaks(peaks: number[], targetCount: number): number[] {
  if (peaks.length === 0 || targetCount <= 0) return [];
  if (peaks.length === targetCount) return peaks;

  const result: number[] = new Array(targetCount);
  const ratio = peaks.length / targetCount;

  for (let i = 0; i < targetCount; i++) {
    const srcIndex = i * ratio;
    const low = Math.floor(srcIndex);
    const high = Math.min(low + 1, peaks.length - 1);
    const frac = srcIndex - low;
    result[i] = peaks[low] * (1 - frac) + peaks[high] * frac;
  }

  return result;
}
